/**
 * Acción de REDUX que permite llevar a cabo todas las operaciones sobre los usuarios 
 */

import { USERS } from '../../../application/types';
import firebaseApp from 'firebase/compat/app';
import 'firebase/compat/auth';
import { settings } from '../../../config/services';
import { roleMirrorRef, roleMirrorData } from '../../lib/role_mirror';
import { resolveAlcance } from '../../lib/user_scope';
import { canAdjust } from '../../lib/rank';

/** Rol global de quien opera, tal como lo guarda el reducer de roles. Sin rol legible → null. */
const callerRoleOf = (state) => {
  try {
    return state().get('roles').get('roles').roles || null;
  } catch (e) {
    return null;
  }
};

const MSG_RANK = 'No puedes cambiar el rol o el alcance de alguien de tu mismo rango o superior';

/**
 * Exporta las funciones para el manejo de los usuarios
 */
export const users = {

  /**
   * Función que permite crear un usuario.
   *
   * Flujo:
   *  1. Verificar permisos del usuario actual.
   *  2. Pre-validar que el email NO exista en Firestore `users/`
   *     (evita un viaje a Firebase Auth si ya está duplicado).
   *  3. Validar que el rol exista.
   *  4. Crear el usuario en Firebase Auth via app secundaria (para no
   *     afectar la sesión del admin que está creando).
   *  5. Crear el doc en `users/` + audit log en un batch.
   *  6. Enviar email de reseteo de contraseña (best-effort).
   *  7. Eliminar la app secundaria para no dejar referencias colgadas.
   *
   * Errores comunes que se convierten en mensajes legibles:
   *  - auth/email-already-in-use → "Ya existe una cuenta con este email"
   *  - auth/invalid-email        → "El email no es válido"
   *  - auth/weak-password        → "La contraseña debe tener al menos 6 caracteres"
   */
  create: ({ roles, user }) => (dispatch, state, firebase) => {

    dispatch({ type: USERS.CREATE.CREATING_USERS });

    return new Promise(async (resolve, reject) => {
      if (!roles?.create) {
        return reject(dispatch({
          type: USERS.CREATE.ERROR,
          code: 'permissions',
          message: 'No tienes permisos para crear usuarios.'
        }));
      }

      const db = firebase().firestore();
      let secondaryApp = null;

      try {
        // ── 1) Pre-validar duplicado en Firestore ─────────────────────
        const existing = await db.collection('users')
          .where('Email', '==', user.email)
          .limit(1)
          .get();
        if (!existing.empty) {
          return reject(dispatch({
            type: USERS.CREATE.ERROR,
            code: 'auth/email-already-in-use',
            message: `Ya existe un usuario registrado con el email "${user.email}".`
          }));
        }

        // ── 2) Validar rol ────────────────────────────────────────────
        const rol = db.collection('roles').doc(user.rol);
        const roleSnap = await rol.get();
        if (!roleSnap.exists) {
          return reject(dispatch({
            type: USERS.CREATE.ERROR,
            code: 'data',
            message: `El rol "${user.rol}" no existe en el sistema.`
          }));
        }

        // ── 3) Crear en Auth via app secundaria ───────────────────────
        const appName = window.btoa(Math.floor(new Date().valueOf() * Math.random()).toString());
        secondaryApp = firebaseApp.initializeApp(settings.firebase.config, appName);
        const secondaryAuth = secondaryApp.auth();

        let createdUser;
        try {
          const cred = await secondaryAuth.createUserWithEmailAndPassword(user.email, user.password);
          createdUser = cred.user;
        } catch (authErr) {
          // Traducir códigos de Firebase Auth a mensajes legibles
          const map = {
            'auth/email-already-in-use': `Ya existe una cuenta con el email "${user.email}".`,
            'auth/invalid-email':        'El email no es válido.',
            'auth/weak-password':        'La contraseña debe tener al menos 6 caracteres.',
            'auth/operation-not-allowed': 'El registro por email/contraseña no está habilitado en este proyecto.'
          };
          return reject(dispatch({
            type: USERS.CREATE.ERROR,
            code: authErr.code,
            message: map[authErr.code] || authErr.message || 'No se pudo crear el usuario.'
          }));
        }

        await createdUser.updateProfile({ displayName: user.name });

        // ── 4) Crear doc en Firestore + audit ─────────────────────────
        const uid = firebase().auth().currentUser.uid;
        const batch = db.batch();

        const log_audit = db.collection('audit').doc();
        const document = db.collection('users').doc(createdUser.uid);

        const after = {
          'Email': user.email,
          'Identificacion': user.id,
          'Nombre completo': user.name,
          'Empresa': db.collection('customers').doc(user.company),
          'Rol': rol,
          // Verificación en 2 pasos: obligatoria para TODOS los usuarios
          // (política global 2026-07). `method` lo registran las Cloud
          // Functions cuando el usuario se enrola; el trigger
          // syncUserMfaClaims espeja este campo a custom claims.
          mfa: { method: null, required: true }
        };
        // Rol de empresa: Alcance 'empresas' siempre, derivado (2026-10-08). Rol
        // global: solo root manda el valor (la pantalla y las reglas lo exigen).
        const alcance = resolveAlcance({ roleData: roleSnap.data(), formAlcance: user.alcance });
        if (alcance) after['Alcance'] = alcance;
        if (user.sinToken) after['SinToken'] = user.sinToken;
        if (user.carpetas) after['Carpetas'] = user.carpetas;

        batch.set(log_audit, { uid, eventType: USERS.CREATE.CREATE_USERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('after').doc(), after);
        batch.set(document, after);
        // Espejo público del rol, en el mismo batch (spec permisos solo por empresa).
        batch.set(roleMirrorRef(db, createdUser.uid), roleMirrorData(rol, after['Alcance']));

        await batch.commit();

        // ── 5) Enviar email de reset (best-effort, no bloquea) ────────
        try {
          await secondaryAuth.sendPasswordResetEmail(user.email);
        } catch (mailErr) {
          console.warn('No se pudo enviar email de reset (usuario creado igual):', mailErr?.message);
        }

        return resolve(dispatch({ type: USERS.CREATE.CREATED_USERS, users: createdUser }));

      } catch (error) {
        return reject(dispatch({
          type: USERS.CREATE.ERROR,
          code: error.code,
          message: error.message
        }));
      } finally {
        // ── 6) Cleanup de la app secundaria ─────────────────────────
        if (secondaryApp) {
          try { await secondaryApp.delete(); } catch (e) { /* ignore */ }
        }
      }
    });
  },

  /**
   * Función que permite consultar la lista de usuarios.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Filtro que se desea aplicar a la consulta
   */
  read: ({ roles, filter }) => async (dispatch, getState, firebase) => {
    dispatch({ type: USERS.READ.READING_USERS });
  
    if (!roles.read) {
      dispatch({ type: USERS.READ.ERROR, code: 0, message: 'Permisos insuficientes para consultar la lista de usuario' });
      return;
    }
  
    try {
      const db = firebase().firestore();
      const collection = db.collection('users');
      const query = filter?.value?.trim()?.length > 0 
        ? collection.where(filter.field, '>=', filter.value).where(filter.field, '<=', filter.value + '\uf8ff')
        : collection;
      const snapshot = await query.get();
      console.log("🚀 ~ read: ~ snapshot:", snapshot)
  
      const users = await Promise.all(
        snapshot.docs.map(async doc => ({
          id: doc.id,
          data: { ...doc.data(), 'Empresa': (await doc.data()?.['Empresa']?.get()) }
        }))
      );
  
      dispatch({ type: USERS.READ.READED_USERS, users });
    } catch (error) {
      dispatch({ type: USERS.READ.ERROR, code: error.code, message: error.message });
    }
  },
  // read: ({ roles, filter }) => ( dispatch, state, firebase ) => {

  //   dispatch({ type: USERS.READ.READING_USERS });

  //   return new Promise( async ( resolve, reject ) => {
  //     if ( roles.read ) {
  //       try {

  //         const db = firebase().firestore();
  //         let query = await (
  //           filter?.value?.trim()?.length > 0 
  //             ? db.collection('users').where( filter.field, '==', filter.value )
  //             : db.collection('users') ).get();

  //         const users = !Array.isArray( query.docs )
  //           ? { id: query.id, data: query.data }
  //           : await Promise.all(
  //             query.docs.map(
  //               async doc => ({
  //                 id: doc.id,
  //                 data: { ...doc.data(), 'Empresa': (await doc.data()?.['Empresa']?.get()) }
  //               })
  //             )
  //           );

  //         resolve(dispatch({ type: USERS.READ.READED_USERS, users }));

  //       } catch ( error ) {
  //         reject(
  //           dispatch({ type: USERS.READ.ERROR, code: error.code, message: error.message })
  //         );
  //       }
  //     } else {
  //       reject(
  //         dispatch({ type: USERS.READ.ERROR, code: 0, message: 'Permisos insuficientes para consultar la lista de usuario' })
  //       )
  //     }
  //   })

  // },

  /**
   * Función que permite actualizar la información de un usuario.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Información del usuario
   * ID del usuario
   */


  update: ({ roles, user, id }) => (dispatch, state, firebase) => {

    dispatch({ type: USERS.UPDATE.UPDATING_USERS });

    return new Promise((resolve, reject) => {
      if (roles.update) {

        const db = firebase().firestore();
        const document = db.collection('users').doc(id);
        const rol = db.collection('roles').doc(user.rol);

        rol.get().then(async query => {
          if (query.exists) {

            const uid = firebase().auth().currentUser.uid;
            const batch = db.batch();

            const log_audit = db.collection('audit').doc();
            const before = (await document.get()).data();
            const after = {
              ...before,
              'Identificacion': user.id,
              'Nombre completo': user.name,
              'Empresa': db.collection('customers').doc(user.company),
              'Rol': rol,
              // Merge del sub-campo para NO pisar mfa.method (lo gestionan
              // las Cloud Functions). `required` siempre true: la
              // verificación en 2 pasos es obligatoria para todos.
              mfa: { ...(before.mfa || {}), required: true }
            };
            // Rol de empresa: 'empresas' siempre (derivado). Rol global: el valor del
            // formulario (solo root), si no el previo; un no-root nunca ensancha.
            const alcance = resolveAlcance({ roleData: query.data(), formAlcance: user.alcance, previousAlcance: before?.Alcance });
            if (alcance) after['Alcance'] = alcance;
            if (user.sinToken) after['SinToken'] = user.sinToken;
            if (user.carpetas) after['Carpetas'] = user.carpetas;

            // Jerarquía (decisión 2026-10-08): un no-root solo cambia el Rol o el Alcance de
            // alguien cuyo rol ACTUAL tenga rango menor. Rol actual ilegible → se rechaza.
            // Antes de cualquier escritura; las reglas de users lo repiten (targetBelowMe).
            const callerRole = callerRoleOf(state);
            const callerIsRoot = callerRole?.documents?.root === true;
            const changesRoleOrScope = before?.Rol?.id !== rol.id || (after['Alcance'] ?? undefined) !== (before?.Alcance ?? undefined);
            if (!callerIsRoot && changesRoleOrScope) {
              let currentRole = null;
              try {
                const currentSnap = before?.Rol?.id ? await db.collection('roles').doc(before.Rol.id).get() : null;
                currentRole = currentSnap?.exists ? currentSnap.data() : null;
              } catch (e) { currentRole = null; }
              if (!currentRole || !canAdjust(callerRole, currentRole)) {
                return reject(Object.assign(
                  new Error(MSG_RANK),
                  dispatch({ type: USERS.UPDATE.ERROR, code: 'rank', message: MSG_RANK })
                ));
              }
            }

            batch.set(log_audit, { uid, eventType: USERS.UPDATE.UPDATE_USERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
            batch.set(log_audit.collection('before').doc(), before);
            batch.set(log_audit.collection('after').doc(), after);
            batch.update(document, after);
            // Espejo idempotente con el Rol y Alcance resultantes; sin Rol no se escribe.
            if (rol?.id) batch.set(roleMirrorRef(db, id), roleMirrorData(rol, after['Alcance']));

            batch.commit().then(() => {
              resolve(dispatch({ type: USERS.UPDATE.UPDATED_USERS }));
            }).catch(
              error => reject(
                dispatch({ type: USERS.UPDATE.ERROR, code: error.code, message: error.message })
              )
            );

          } else {
            reject({ code: 'data', message: 'El rol seleccionado no es valido' });
          }
        }).catch(error => reject(
          dispatch({ type: USERS.READ.ERROR, code: error.code, message: error.message })
        ))

      }
    })

  },

  /**
   * Función que permite eliminar un usuario por completo (doc de Firestore,
   * cuenta de Firebase Auth y membresías en clientes).
   *
   * SOLO root: la UI oculta el botón para los demás roles, pero la
   * verificación real vive en la callable `deleteUserAccount` (Admin SDK),
   * que valida server-side que quien llama tenga documents.root === true.
   *
   * @param {string} id uid del usuario a eliminar
   */
  delete: ({ id }) => async (dispatch, state, firebase) => {

    dispatch({ type: USERS.DELETE.DELETING_USERS });

    try {
      const callable = firebase().app().functions().httpsCallable('deleteUserAccount');
      await callable({ uid: id });
      return dispatch({ type: USERS.DELETE.DELETED_USERS });
    } catch (error) {
      dispatch({ type: USERS.DELETE.ERROR, code: error.code, message: error.message });
      throw error;
    }

  }

}